import type { AdminFoodRequest } from '@steado/admin-contract/foods';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CheckIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth/auth-context';

import { recentlyAnsweredQueryOptions } from './food-requests-query';
import { ago, outcomeOf, plural } from './request-copy';
import { KindBadge } from './review-parts';

/**
 * The queue with nothing in it that needs a person: what is still being
 * researched (it joins the queue when its sources are in), and the latest
 * answers, each linking to the History its answer wrote.
 */
export function EmptyQueue({
  researching,
  onShowAnswered,
}: {
  readonly researching: readonly AdminFoodRequest[];
  readonly onShowAnswered: () => void;
}) {
  const { api } = useAuth();
  const recent = useQuery(recentlyAnsweredQueryOptions(api));
  const answered = recent.data ?? [];

  return (
    <section
      aria-labelledby="empty-title"
      className="overflow-hidden rounded-xl border border-border bg-card"
    >
      <div className="flex flex-col items-center gap-1.5 px-[18px] pt-12 pb-11 text-center">
        <span
          aria-hidden="true"
          className="mb-2.5 flex size-10 items-center justify-center rounded-full bg-ok-bg"
        >
          <CheckIcon className="size-[18px] text-ok-fg" />
        </span>
        <h2 id="empty-title" className="text-[17px] font-semibold tracking-[-0.01em] text-foreground">
          Nothing needs you now.
        </h2>
        <p className="max-w-[70ch] text-[13px] text-muted-foreground">
          {researching.length === 0
            ? 'Nothing is being researched either.'
            : `${plural(researching.length, 'food')} ${researching.length === 1 ? 'is' : 'are'} still being researched: ${researching.map((request) => request.name).join(', ')}.`}
        </p>
      </div>

      {researching.length === 0 ? null : (
        <>
          <div className="border-t border-border px-[18px] pt-3.5 pb-1.5">
            <h2 className="text-[15px] font-semibold text-foreground">Being researched</h2>
          </div>
          <ul>
            {researching.map((request, index) => (
              <li
                key={request.id}
                className={
                  'flex flex-wrap items-center gap-x-2.5 gap-y-1 px-[18px] py-2.5' +
                  (index === 0 ? '' : ' border-t border-border')
                }
              >
                <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-info-fg" />
                <span className="font-medium text-foreground">{request.name}</span>
                <KindBadge kind={request.kind} />
                <span className="text-[13px] text-muted-foreground">
                  Researching, queued {ago(request.updatedAt)}
                </span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {plural(request.userCount, 'person', 'people')}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="flex items-center border-t border-border px-[18px] pt-3.5 pb-1.5">
        <h2 className="text-[15px] font-semibold text-foreground">Recently answered</h2>
        <button
          type="button"
          onClick={onShowAnswered}
          className="ml-auto inline-flex min-h-6 items-center text-xs text-foreground underline underline-offset-2 hover:text-destructive"
        >
          All answered requests
        </button>
      </div>
      {answered.length === 0 ? (
        <p className="px-[18px] pt-1 pb-3.5 text-[13px] text-muted-foreground">
          {recent.isPending ? 'Loading…' : 'No request has been answered yet.'}
        </p>
      ) : (
        <ul>
          {answered.map((request, index) => (
            <li
              key={request.id}
              className={
                'flex flex-wrap items-center gap-x-3 gap-y-1 px-[18px] py-2.5' +
                (index === 0 ? '' : ' border-t border-border')
              }
            >
              <Badge size="sm" tone={request.status === 'rejected' ? 'mute' : 'ok'}>
                {request.status === 'rejected' ? 'Rejected' : 'Approved'}
              </Badge>
              <Link
                to="/food-requests/$requestId"
                params={{ requestId: request.id }}
                className="font-medium text-foreground no-underline hover:text-destructive"
              >
                {request.name}
              </Link>
              <span className="text-[13px] text-muted-foreground">{outcomeOf(request)}</span>
              <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                {request.answeredAt === null ? null : ago(request.answeredAt)}
                {request.status === 'resolved' && request.resolvedFoodId !== null ? (
                  <Link
                    to="/foods/$foodId"
                    params={{ foodId: request.resolvedFoodId }}
                    search={{ tab: 'history' }}
                    className="font-medium text-info-fg no-underline hover:underline"
                  >
                    {request.resolvedFoodName ?? 'The food'}’s History
                  </Link>
                ) : (
                  <span>No catalog change</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
